import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { SeoLink } from 'dibs-elements/exports/SeoLink';

import { getByDocCreator } from '../helpers/getByDocCreator';
import { getGuidancePriceAmount } from './pricingGuidanceHelpers';

import {
    PdpSharedPricingGuidanceCopy_item$key,
    PdpSharedPricingGuidanceCopy_item$data,
} from './__generated__/PdpSharedPricingGuidanceCopy_item.graphql';

import styles from './styles/PdpSharedPricingGuidanceCopy.scss';

type Props = {
    item: PdpSharedPricingGuidanceCopy_item$key;
    currency?: string;
};

const getItemTypeForCopyText = ({
    iconicDesign,
    creators,
    classification,
}: PdpSharedPricingGuidanceCopy_item$data): ReactNode | string => {
    if (iconicDesign) {
        const iconicDesignName = iconicDesign.displayName;
        return iconicDesignName ? (
            <FormattedMessage
                id="pdpPricingGuidance.iconicDesignPlural"
                defaultMessage="<designLink>{design}s</designLink>"
                values={{
                    design: iconicDesignName,
                    designLink: (content: ReactNode[]) => (
                        <SeoLink
                            linkData={{
                                path: iconicDesign.browsePageLinkData.path || '',
                                isLinkable: iconicDesign.browsePageLinkData.isLinkable,
                            }}
                        >
                            {content}
                        </SeoLink>
                    ),
                }}
            />
        ) : (
            ''
        );
    }

    const lowestCategory = (classification?.categories || []).find(c => c?.precedence === 1);
    const lowestCategoryName = lowestCategory?.category?.name || '';

    const creatorName = getByDocCreator(creators);
    if (creatorName && creatorName.toLowerCase() !== 'unknown') {
        return (
            <FormattedMessage
                id="pdpPricingGuidance.categoryWithCreator"
                defaultMessage="{category} by {creator}"
                values={{ category: lowestCategoryName, creator: creatorName }}
            />
        );
    }

    return (
        <FormattedMessage
            id="pdpPricingGuidance.category"
            defaultMessage="{category}"
            values={{ category: lowestCategoryName }}
        />
    );
};

const itemFragment = graphql`
    fragment PdpSharedPricingGuidanceCopy_item on Item {
        ...pricingGuidanceHelpers_item
        pricingGuidance {
            medianPrice
            priceRange
            pricingGuidanceType
        }
        iconicDesign {
            displayName
            browsePageLinkData {
                isLinkable
                path
            }
        }
        creators {
            attribution
            creator {
                name
                displayName
            }
        }
        classification {
            categories {
                precedence
                category {
                    name
                    browseUrl
                }
            }
        }
    }
`;

export const PdpSharedPricingGuidanceCopy: FC<Props> = ({ item: itemRef, currency }) => {
    const item = useFragment(itemFragment, itemRef);
    const { priceRange, pricingGuidanceType, medianPrice } = item?.pricingGuidance || {};

    if (!priceRange) {
        return null;
    }

    return (
        <FormattedMessage
            id="PdpSharedPricingGuidanceCopy.copyContent"
            defaultMessage={`{range, select,
                OUT_OF_RANGE_LOW {This item is priced <green>{lessPrice} less than the median</green> {guidanceType}.}
                LOWER_RANGE {This item is priced <green>{lessPrice} less than the median</green> {guidanceType}.}
                NEAR_MEDIAN_LOW {This item is priced <green>{lessPrice} less than the median</green> {guidanceType}.}
                MEDIAN {This item's price is equal to the median {guidanceType}.}
                NEAR_MEDIAN_HIGH {This item is priced near the median {guidanceType}.}
                HIGHER_RANGE {This item is priced in the higher range {guidanceType}.}
                OUT_OF_RANGE_HIGH {This item is priced in the higher range {guidanceType}.}
                other {}
            }`}
            values={{
                range: priceRange,
                type: getItemTypeForCopyText(item),
                lessPrice:
                    currency && priceRange.includes('LOW') ? (
                        <FormattedNumber
                            key="lessPrice"
                            style="currency"
                            minimumFractionDigits={0}
                            maximumFractionDigits={0}
                            value={
                                (medianPrice || 0) -
                                Math.floor(getGuidancePriceAmount({ item, currency }))
                            }
                            currency={currency}
                        />
                    ) : (
                        ''
                    ),
                green: (content: ReactNode[]) =>
                    currency ? <span className={styles.greenText}>{content}</span> : content,
                guidanceType: (
                    <FormattedMessage
                        id="PdpSharedPricingGuidanceCopy.guidanceType"
                        defaultMessage={`{guidanceType, select,
                            RECENT_SOLD {of {type} sold recently on 1stDibs}
                            AVAILABLE_PRICING {of {type} available for sale on 1stDibs}
                            other {}
                        }`}
                        values={{
                            guidanceType: pricingGuidanceType,
                            type: getItemTypeForCopyText(item),
                        }}
                    />
                ),
            }}
        />
    );
};
